import { useEffect, useState } from 'react'
import type { CatalogItemFilters } from '~/api/catalog'
import type { PartCount } from '~/api/types'
import { Button } from '~/components/ui/button'
import { FormChip } from '~/components/ui/form-chip'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Toggle } from '~/components/ui/toggle'
import { cn } from '~/lib/utils'

// 카탈로그 워크벤치 좌측 필터 패널(KRDS·sticky). 검색(디바운스)·PART 다중선택 칩·
// 분류/규모/단계/데이터유형 세그먼트·결정형 토글 + 활성 필터 칩·초기화.
// 필터 변경은 상위에서 offset 0으로 리셋한다.

// 세그먼트 옵션. 값은 백엔드 필터 파라미터와 정렬한다(빈 값 = 전체).
const CLASSIFICATION_SEGS = [
    { value: '', label: '전체' },
    { value: '선행요건', label: '선행요건' },
    { value: 'HACCP관리', label: 'HACCP관리' },
]
const SCALE_SEGS = [
    { value: '', label: '전체' },
    { value: '일반', label: '일반' },
    { value: '소규모', label: '소규모' },
]
const ASSESSMENT_SEGS = [
    { value: '', label: '전체' },
    { value: '인증', label: '인증' },
    { value: '정기', label: '정기' },
]
const DATA_TYPE_SEGS = [
    { value: '', label: '전체' },
    { value: '정량', label: '정량' },
    { value: '정성', label: '정성' },
]

// 활성 필터 칩 라벨(표시용 한글).
const SEG_LABELS: Record<string, Record<string, string>> = {
    classification: { 선행요건: '선행요건', HACCP관리: 'HACCP관리' },
    scale: { 일반: '일반', 소규모: '소규모' },
    assessmentType: { 인증: '인증', 정기: '정기' },
    dataType: { 정량: '정량', 정성: '정성' },
}

export function FilterPanel({
    filters,
    onChange,
    onReset,
    parts,
    total,
}: {
    filters: CatalogItemFilters
    // 부분 갱신(병합). 호출 시 상위에서 offset 0으로 리셋한다.
    onChange: (patch: Partial<CatalogItemFilters>) => void
    onReset: () => void
    parts: PartCount[]
    total: number
}) {
    // 검색은 300ms 디바운스. 외부에서 초기화되면(q 비움) 입력도 동기화한다.
    const [q, setQ] = useState(filters.q ?? '')
    useEffect(() => {
        setQ(filters.q ?? '')
    }, [filters.q])
    useEffect(() => {
        const t = setTimeout(() => {
            const next = q.trim()
            if (next !== (filters.q ?? '')) onChange({ q: next })
        }, 300)
        return () => clearTimeout(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q])

    const selectedParts = filters.part ?? []
    // PART 토글: 선택돼 있으면 제거, 아니면 추가. 항상 offset 0 리셋(onChange).
    const togglePart = (part: string) => {
        const next = selectedParts.includes(part)
            ? selectedParts.filter((p) => p !== part)
            : [...selectedParts, part]
        onChange({ part: next })
    }

    const activeChips = buildActiveChips(filters)

    return (
        <aside className='lg:sticky lg:top-4 lg:h-fit'>
            <div className='border-border bg-card rounded-[var(--radius)] border shadow-xs'>
                <div className='border-border flex items-center justify-between border-b px-4 py-3'>
                    <span className='text-foreground text-sm font-bold'>
                        필터
                    </span>
                    <span className='text-muted-foreground text-xs'>
                        {total.toLocaleString()}개 일치
                    </span>
                </div>

                <div className='space-y-5 p-4'>
                    {/* 검색 */}
                    <div>
                        <Label htmlFor='catalog-q'>검색</Label>
                        <Input
                            id='catalog-q'
                            className='mt-1 h-9'
                            placeholder='제목·평가로직·세부내용'
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>

                    {/* PART 다중선택(KRDS form-chip) — 20여 개라 스크롤 영역에 담는다 */}
                    <div>
                        <div className='mb-1.5 flex items-center justify-between'>
                            <Label className='mb-0'>PART</Label>
                            {selectedParts.length > 0 ? (
                                <button
                                    type='button'
                                    onClick={() => onChange({ part: [] })}
                                    className='text-muted-foreground hover:text-foreground text-xs font-bold'>
                                    {selectedParts.length}개 선택 · 전체해제
                                </button>
                            ) : null}
                        </div>
                        <div className='flex max-h-[176px] flex-wrap gap-1.5 overflow-y-auto'>
                            {parts.map((p) => (
                                <FormChip
                                    key={p.part}
                                    selected={selectedParts.includes(p.part)}
                                    onToggle={() => togglePart(p.part)}
                                    count={p.count}>
                                    {p.part || '(없음)'}
                                </FormChip>
                            ))}
                        </div>
                    </div>

                    <Segmented
                        label='분류'
                        options={CLASSIFICATION_SEGS}
                        value={filters.classification ?? ''}
                        onChange={(v) => onChange({ classification: v })}
                    />
                    <Segmented
                        label='규모'
                        options={SCALE_SEGS}
                        value={filters.scale ?? ''}
                        onChange={(v) => onChange({ scale: v })}
                    />
                    <Segmented
                        label='단계'
                        options={ASSESSMENT_SEGS}
                        value={filters.assessmentType ?? ''}
                        onChange={(v) => onChange({ assessmentType: v })}
                    />
                    <Segmented
                        label='데이터 유형'
                        options={DATA_TYPE_SEGS}
                        value={filters.dataType ?? ''}
                        onChange={(v) => onChange({ dataType: v })}
                    />

                    {/* 결정형 토글 */}
                    <div className='border-border bg-muted/40 flex items-center justify-between rounded-[var(--radius)] border px-3 py-2'>
                        <label
                            htmlFor='filter-deterministic'
                            className='text-foreground cursor-pointer text-sm font-bold'>
                            결정형만
                        </label>
                        <Toggle
                            id='filter-deterministic'
                            aria-label='결정형만 보기'
                            checked={filters.deterministic === true}
                            onCheckedChange={(on) =>
                                onChange({
                                    deterministic: on ? true : undefined,
                                })
                            }
                        />
                    </div>
                </div>

                {/* 활성 필터 칩 + 초기화 */}
                {activeChips.length > 0 ? (
                    <div className='border-border border-t p-4'>
                        <div className='mb-2 flex items-center justify-between'>
                            <span className='krds-label'>활성 필터</span>
                            <Button
                                variant='tertiary'
                                size='sm'
                                className='h-7 px-2'
                                onClick={onReset}>
                                초기화
                            </Button>
                        </div>
                        <div className='flex flex-wrap gap-1.5'>
                            {activeChips.map((chip) => (
                                <button
                                    key={chip.key}
                                    type='button'
                                    onClick={() => onChange(chip.clear)}
                                    className='border-primary/30 bg-accent text-accent-foreground inline-flex items-center gap-1 rounded-[4px] border px-2 py-0.5 text-[13px] font-bold'>
                                    {chip.label}
                                    <span aria-hidden className='text-xs'>
                                        ✕
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>
        </aside>
    )
}

function Segmented({
    label,
    options,
    value,
    onChange,
}: {
    label: string
    options: { value: string; label: string }[]
    value: string
    onChange: (v: string) => void
}) {
    return (
        <div>
            <Label>{label}</Label>
            <div className='border-input mt-1.5 inline-flex w-full overflow-hidden rounded-[var(--radius)] border'>
                {options.map((o, i) => {
                    const active = value === o.value
                    return (
                        <button
                            key={o.value || '_all'}
                            type='button'
                            aria-pressed={active}
                            onClick={() => onChange(o.value)}
                            className={cn(
                                'flex-1 px-2 py-1 text-[12px] font-medium transition-colors',
                                i > 0 && 'border-input border-l',
                                active
                                    ? 'bg-accent text-accent-foreground'
                                    : 'bg-card text-muted-foreground hover:bg-secondary hover:text-foreground'
                            )}>
                            {o.label}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

// 활성 필터 → 칩 목록(각 칩은 해당 필터만 비우는 patch를 들고 있다).
function buildActiveChips(filters: CatalogItemFilters) {
    const chips: {
        key: string
        label: string
        clear: Partial<CatalogItemFilters>
    }[] = []
    if (filters.q)
        chips.push({
            key: 'q',
            label: `검색: ${filters.q}`,
            clear: { q: '' },
        })
    // 선택된 PART는 각각 개별 제거 칩으로(클릭 시 해당 PART만 해제).
    for (const part of filters.part ?? [])
        chips.push({
            key: `part:${part}`,
            label: `PART: ${part || '(없음)'}`,
            clear: { part: (filters.part ?? []).filter((p) => p !== part) },
        })
    for (const seg of [
        'classification',
        'scale',
        'assessmentType',
        'dataType',
    ] as const) {
        const v = filters[seg]
        if (v)
            chips.push({
                key: seg,
                label: SEG_LABELS[seg]?.[v] ?? v,
                clear: { [seg]: '' },
            })
    }
    if (filters.deterministic === true)
        chips.push({
            key: 'deterministic',
            label: '결정형',
            clear: { deterministic: undefined },
        })
    return chips
}
